import type { Org } from '@mend/types/org';
import type { User } from '@mend/types/user';
import type { ButtonProps, ChipProps } from '@mui/material';
import type {
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import type {
  AskAboutRebookingResponse,
  MiaMessageChannel,
  OfferableSlot,
  OfferSlotToWaitlistResponse,
  PreviewMessageContentResponse,
  RebookingCandidateStatus,
  MiaOrgConfig,
  RiskModelExplainer,
  UnconfirmedRow,
  UnconfirmedRowStatus,
  SendPreparedRequest,
  SendPreparedResponse,
  SendRemindersRequest,
  SendRemindersResponse,
  SetReadinessConfigRequest,
} from '#services/api/mia.ts';
import type {
  PatientReadinessDetailsResponse,
  RescheduleRow,
} from '#services/api/patient-readiness.ts';
import type { GetMeResponsePayload } from '#services/api/user.ts';

import * as React from 'react';
import {
  faBell,
  faBolt,
  faCalendarPlus,
  faChartLineUp,
  faCheck,
  faCircleQuestion,
  faPaperPlane,
  faInfoCircle,
  faMessageBot,
  faPaperPlaneTop,
  faShieldCheck,
  faSliders,
  faSparkles,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck as faCircleCheckSolid } from '@fortawesome/pro-solid-svg-icons';
import { Dialog, FontAwesomeScalableIcon } from '@mend/mui';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  RadioGroup,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPro, gridClasses } from '@mui/x-data-grid-pro';
import { usePostHog } from '@posthog/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { useSearchParams } from 'react-router';
import { z } from 'zod';

import { useLocalState } from '#hooks/local-state.ts';
import {
  miaMessagePreview,
  offerableSlots,
  patientReadinessDetails,
  readinessConfig,
  riskModelExplainer,
} from '#lib/react-query/queries.ts';
import {
  approveSlotFill,
  askAboutRebooking,
  getClaimActionErrorMessage,
  getCouldntBookMessage,
  getOfferSlotErrorMessage,
  offerSlotToWaitlist,
  releaseSlotFillClaim,
  sendPrepared,
  sendReminders,
  setReadinessConfig,
} from '#services/api/mia.ts';
import {
  getUnconfirmedRowRiskProbability,
  getUnconfirmedRowRiskSortValue,
} from '#services/api/patient-readiness.ts';
import { canEditReadinessThreshold } from '#utils/roles.ts';
import * as DataAgent from '../DataAgent.tsx';
import * as BriefingCard from './BriefingCard.tsx';
import {
  attendanceRiskCopy,
  deriveFallbackThreshold,
  fallbackFlaggedShare,
  selectFlaggedRows,
} from './Readiness.utils.ts';
import * as ReadinessBucketCard from './ReadinessBucketCard.tsx';
import { RebookDialog } from './RebookDialog.tsx';
import { RequestConfirmationDialog } from './RequestConfirmationDialog.tsx';
import { RiskModelDialog, type ExplainerState, type ThresholdState } from './RiskModelDialog.tsx';

const readinessBuckets = ['unconfirmed', 'reschedule', 'waitlist'] as const;
type ReadinessBucket = (typeof readinessBuckets)[number];

type DataAgentStatus = 'auto-pending' | 'auto-ready' | 'auto-viewed';
type BulkConfirmationDialogSource = 'briefing' | 'card';

interface BriefingOutreachResult {
  sentCount: number;
  isFullSuccess: boolean;
}

const unconfirmedBucket = {
  key: 'unconfirmed',
  color: 'error',
  // Mirrors `unconfirmedWorklistDaysAhead` in patient-readiness.ts — change them together.
  emphasis: 'Next 2 days',
} as const;

const waitlistBucket = {
  key: 'waitlist',
  color: 'quaternary',
  metricLabel: 'Appointment Slots to Fill',
  emphasis: 'Due to late cancellation',
} as const;

const rescheduleBucket = {
  key: 'reschedule',
  color: 'warning',
  metricLabel: 'Patients Who Need to Rebook',
  emphasis: 'Last 7 days',
} as const;

/**
 * Narrows the explainer query to what the modal renders. A settled query with no data is a failed
 * read, which the modal must report as an outage rather than as a fact about the practice.
 */
function toExplainerState(query: {
  data: RiskModelExplainer | undefined;
  isPending: boolean;
}): ExplainerState {
  if (query.isPending) {
    return { status: 'loading' };
  }

  return query.data ? { status: 'ready', explainer: query.data } : { status: 'failed' };
}

/**
 * Resolves the threshold the briefing and modal both work from.
 *
 * A `saved` or `recommended` threshold is a real per-org number and is used exactly as given. Only
 * the global `fallback` is replaced — by a floor read off this org's own loaded predictions — and
 * only when there is a distribution to read it from; an org with nothing scored keeps the server's
 * constant, which no briefing state consults anyway.
 */
function toThresholdState(
  query: { data: MiaOrgConfig | undefined; isPending: boolean },
  atRiskRows: UnconfirmedRow[]
): ThresholdState {
  if (query.isPending) {
    return { status: 'loading' };
  }

  if (!query.data) {
    return { status: 'failed' };
  }

  const { riskThreshold, thresholdSource } = query.data;
  const derived =
    thresholdSource === 'fallback'
      ? deriveFallbackThreshold(atRiskRows, fallbackFlaggedShare)
      : null;

  return derived === null
    ? { status: 'ready', threshold: riskThreshold, basis: thresholdSource }
    : { status: 'ready', threshold: derived, basis: 'derived' };
}

function isReadinessBucket(bucket: string | null): bucket is ReadinessBucket {
  return (readinessBuckets as readonly string[]).includes(bucket ?? '');
}

function getSelectedBucket(bucket: string | null): ReadinessBucket {
  return isReadinessBucket(bucket) ? bucket : 'unconfirmed';
}

// TODO(multi-channel): deliberately SMS-gated: the server's bulk ask fans out to every reachable
// channel (email included, behind its MIA_EMAIL_ENABLED flag), but this UI only offers outreach
// to patients with a phone on file. When the product goes multi-channel here, this becomes phone
// OR email — and the "No mobile number" cell, the SMS-only single-send dialog, and the bulk
// dialog's "Sent via SMS" copy all need to move with it.
function isSmsReachableRebookingRow(row: RescheduleRow): boolean {
  return row.status === 'needs_outreach' && Boolean(row.patientPhone);
}

function selectReadinessDetails(data: PatientReadinessDetailsResponse) {
  return {
    ...data,
    actionableRescheduleRows: data.reschedule.filter(isSmsReachableRebookingRow),
  };
}

/**
 * Why the briefing can't rank what's at risk. Both cases leave a real backlog unranked, so they read
 * as the same shape of briefing — but never as the same sentence, because "your practice has no
 * predictions yet" and "I couldn't read your threshold" are not the same admission.
 */
type UnrankableReason = 'no-predictions' | 'threshold-unavailable';

/**
 * Which of the four things the briefing has to say. Ordered by precedence: a send this session is
 * the freshest news, and "I can't rank these" outranks the ranking states because ranking is what it
 * lacks. `unrankable` is deliberately distinct from `all-clear` — an org with unconfirmed
 * appointments it couldn't rank has plenty at risk, so telling it things look good would be a lie.
 */
type BriefingState =
  | { type: 'outreach-sent'; outreachResult: BriefingOutreachResult }
  | { type: 'unrankable'; reason: UnrankableReason }
  | { type: 'appointments-at-risk' }
  | { type: 'all-clear' };

/**
 * `scoredCount` and `flaggedCount` are separate inputs on purpose. Under a threshold, "nothing
 * flagged" no longer implies "nothing scored": a practice whose every prediction sits below its own
 * threshold is genuinely all-clear, and must not be told its predictions haven't arrived. (The
 * relative-share ranking this replaced could infer one from the other, because its floor of one
 * guaranteed a flag whenever anything was scored.)
 */
function getBriefingState(params: {
  outreachResult: BriefingOutreachResult | null;
  atRiskCount: number;
  scoredCount: number;
  flaggedCount: number;
  isThresholdResolved: boolean;
}): BriefingState {
  if (params.outreachResult) {
    return { type: 'outreach-sent', outreachResult: params.outreachResult } as const;
  }

  // Both unrankable cases are gated on there being something to rank: with nothing at risk, an
  // unknown threshold changes nothing and all-clear is the honest answer.
  if (params.atRiskCount > 0) {
    if (!params.isThresholdResolved) {
      return { type: 'unrankable', reason: 'threshold-unavailable' } as const;
    }

    if (params.scoredCount === 0) {
      return { type: 'unrankable', reason: 'no-predictions' } as const;
    }
  }

  if (params.flaggedCount > 0) {
    return { type: 'appointments-at-risk' } as const;
  }

  return { type: 'all-clear' } as const;
}

/**
 * The briefing's send entry point. Shared by the ranked and unrankable states: a practice we can't
 * rank still has appointments to confirm, so the send path can't depend on the ranking.
 */
function ReviewOutreachButton(props: { disabled: boolean; onClick: () => void }) {
  return (
    <Button
      variant="contained"
      color="quaternary"
      size="large"
      startIcon={<FontAwesomeScalableIcon icon={faPaperPlaneTop} />}
      disabled={props.disabled}
      onClick={props.onClick}
    >
      Review the outreach
    </Button>
  );
}

const afternoonStartHour = 12;
const eveningStartHour = 18;

function getTimeOfDayGreeting(date: Date): string {
  const hour = date.getHours();

  if (hour >= eveningStartHour) {
    return 'Good evening';
  }

  if (hour >= afternoonStartHour) {
    return 'Good afternoon';
  }

  return 'Good morning';
}

export function Readiness(props: { me: GetMeResponsePayload }) {
  const query = useQuery({
    ...patientReadinessDetails({ orgId: props.me.org.id }),
    select: selectReadinessDetails,
  });
  const offerableSlotsQuery = useQuery(offerableSlots({ orgId: props.me.org.id }));
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const riskModelExplainerQuery = useQuery(riskModelExplainer({ orgId: props.me.org.id }));
  const readinessConfigQueryOptions = readinessConfig({ orgId: props.me.org.id });
  const readinessConfigQuery = useQuery(readinessConfigQueryOptions);
  const [isAutoRunDialogOpen, setAutoRunDialogOpen] = React.useState(false);
  const [isAtRiskPatientsDrawerOpen, setAtRiskPatientsDrawerOpen] = React.useState(false);
  const [isBulkConfirmationDialogOpen, setBulkConfirmationDialogOpen] = React.useState(false);
  const [isRiskModelDialogOpen, setRiskModelDialogOpen] = React.useState(false);
  const [briefingOutreachResult, setBriefingOutreachResult] =
    React.useState<BriefingOutreachResult | null>(null);
  const [greeting] = React.useState(() => getTimeOfDayGreeting(new Date()));
  const atRiskPatientsAgentStatusRef = React.useRef<DataAgentStatus>('auto-pending');
  const bulkConfirmationDialogSourceRef = React.useRef<BulkConfirmationDialogSource | null>(null);
  const tabOpenedAtRef = React.useRef(Date.now());

  // Only ever fired from the admin-gated Save in the risk-model modal — the server's
  // `orgAdminProcedure` is the second line, not the first.
  const setReadinessConfigMutation = useMutation({
    mutationFn: (request: SetReadinessConfigRequest) => setReadinessConfig(request),
    onSuccess: (savedConfig) => {
      // Captured on success, not on click — a failed POST must not be recorded as a saved
      // threshold — and with the persisted value, which is the fact the event claims.
      posthog.capture('mia_readiness_threshold_saved', { threshold: savedConfig.riskThreshold });
      enqueueSnackbar('Your risk threshold is saved.', { variant: 'success' });
      // The response is the authoritative config, so write it in rather than refetching.
      queryClient.setQueryData(readinessConfigQueryOptions.queryKey, savedConfig);
      setRiskModelDialogOpen(false);
    },
    onError: () => {
      enqueueSnackbar('Your risk threshold could not be saved. Please try again later.', {
        variant: 'error',
      });
    },
  });

  function handleAtRiskPatientsAutoDetectResponse() {
    // Track readiness only while the automatic explanation is unseen; opening the drawer marks
    // the session as viewed, and follow-up responses from the same session are ignored.
    if (atRiskPatientsAgentStatusRef.current !== 'auto-pending') {
      return;
    }

    atRiskPatientsAgentStatusRef.current = 'auto-ready';
    posthog.capture('mia_readiness_risk_explanation_ready', {
      ms_since_tab_opened: Date.now() - tabOpenedAtRef.current,
    });
  }

  function closeAtRiskPatientsDrawer() {
    setAtRiskPatientsDrawerOpen(false);
  }

  function selectBucket(bucket: ReadinessBucket) {
    posthog.capture('mia_readiness_bucket_selected', { bucket });
    setSearchParams((currentSearchParams) => {
      const nextSearchParams = new URLSearchParams(currentSearchParams);
      nextSearchParams.set('bucket', bucket);
      return nextSearchParams;
    });
  }

  // Shared by the unconfirmed card's `Root` (for its interactive descendants) and its card-scoped
  // CTA, so a click on either while the card isn't selected yet selects it the same way.
  function selectUnconfirmedCard() {
    selectBucket(unconfirmedBucket.key);
  }

  function selectRescheduleCard() {
    selectBucket(rescheduleBucket.key);
  }

  function openAutoRunDialog() {
    posthog.capture('mia_readiness_waitlist_dialog_opened', {
      bucket: unconfirmedBucket.key,
      source: 'auto_run_switch',
    });
    setAutoRunDialogOpen(true);
  }

  React.useEffect(() => {
    posthog.capture('mia_readiness_tab_opened');
  }, [posthog]);

  // Deliberately not gated on offerableSlotsQuery — Slot-Fill is a bolt-on; the
  // waitlist card starts as its placeholder and fills in when that read lands.
  if (query.isLoading) {
    return (
      <Card sx={{ padding: 2, width: '100%' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2,
            mb: 3,
          }}
        >
          <Skeleton variant="rounded" height={180} />
          <Skeleton variant="rounded" height={180} />
          <Skeleton variant="rounded" height={180} />
        </Box>

        <Skeleton variant="rounded" height={200} />
      </Card>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
        Readiness summary could not load. Please try again later.
      </Alert>
    );
  }

  const selectedBucket = getSelectedBucket(searchParams.get('bucket'));
  const canEditThreshold = canEditReadinessThreshold(props.me.user.role);
  // Not surfaced as wording (the UI speaks the "attendance risk" umbrella) — it rides along on the
  // modal-opened event so the analytics can tell the two models' audiences apart.
  const riskKind = query.data.riskKind;
  const unconfirmedRows = query.data.unconfirmed;
  // Confirmed rows stay in the grid as outcome context, but ranking is over appointments that could
  // still be missed — a confirmed one needs no outreach however the model scored it.
  const atRiskRows = unconfirmedRows.filter((row) => row.status !== 'confirmed');
  const unconfirmedCount = atRiskRows.length;
  // Every scored row's probability. Feeds the briefing's flagging and — via the modal — the live
  // count its slider previews, so both answer "who clears this threshold" from the same numbers.
  const riskProbabilities = atRiskRows
    .map(getUnconfirmedRowRiskProbability)
    .filter((probability): probability is number => probability !== null);
  const thresholdState = toThresholdState(readinessConfigQuery, atRiskRows);
  // The briefing narrows to what clears the org's threshold, but only the briefing does: the bucket
  // card counts every at-risk appointment (its label claims exactly that, and the grid beneath it
  // lists them all) and the bulk CTA still reaches all of them. The threshold picks what to talk
  // about first, and is not allowed to shrink what staff can act on.
  const flaggedRows =
    thresholdState.status === 'ready'
      ? selectFlaggedRows(atRiskRows, thresholdState.threshold)
      : [];
  const flaggedCount = flaggedRows.length;
  const briefingState = getBriefingState({
    outreachResult: briefingOutreachResult,
    atRiskCount: unconfirmedCount,
    scoredCount: riskProbabilities.length,
    flaggedCount,
    isThresholdResolved: thresholdState.status === 'ready',
  });
  const notContactedRows = unconfirmedRows.filter((row) => row.status === 'not_contacted');
  const rescheduleRows = query.data.reschedule;
  const actionableRescheduleRows = query.data.actionableRescheduleRows;
  const rescheduleCount = rescheduleRows.length;
  // A slot-fill outage never blocks the rest of Readiness.
  const slotFillData = offerableSlotsQuery.isSuccess ? offerableSlotsQuery.data : null;
  const slotFillEnabled = slotFillData?.enabled ?? false;
  const offerableSlotRows = slotFillData?.slots ?? [];
  const flaggedAppointmentIds = flaggedRows.map((row) => row.appointmentId).join(', ');
  const dataAgentInitialPrompt =
    briefingState.type !== 'appointments-at-risk'
      ? undefined
      : flaggedCount === 1
        ? 'Why is the patient of this appointment at risk?'
        : 'Why are the patients of these appointments at risk?';
  const dataAgentInitialPrivateContext =
    briefingState.type === 'appointments-at-risk'
      ? `Use the following appointment IDs: ${flaggedAppointmentIds}.`
      : undefined;

  function openBulkConfirmationDialogFromBriefing() {
    if (notContactedRows.length === 0) {
      return;
    }

    posthog.capture('mia_readiness_confirmation_dialog_opened', {
      bucket: 'unconfirmed',
      appointment_count: notContactedRows.length,
      action_scope: 'card',
      source: 'briefing',
    });
    bulkConfirmationDialogSourceRef.current = 'briefing';
    setBulkConfirmationDialogOpen(true);
  }

  function handleBulkConfirmationDialogOpenChange(isOpen: boolean) {
    setBulkConfirmationDialogOpen(isOpen);
    bulkConfirmationDialogSourceRef.current = isOpen ? 'card' : null;
  }

  function handleBulkConfirmationHandled(response: SendRemindersResponse) {
    if (bulkConfirmationDialogSourceRef.current !== 'briefing' || response.sent.length === 0) {
      return;
    }

    setBriefingOutreachResult({
      sentCount: response.sent.length,
      isFullSuccess: response.failed.length === 0 && response.skipped.length === 0,
    });
  }

  function openAtRiskPatientsDrawerFromBriefing() {
    posthog.capture('mia_readiness_data_agent_opened', {
      source: 'briefing_why_patients',
      agent_status: atRiskPatientsAgentStatusRef.current,
    });
    // Keep the automatic session mounted even if its first response is still pending.
    atRiskPatientsAgentStatusRef.current = 'auto-viewed';
    setAtRiskPatientsDrawerOpen(true);
  }

  function openRiskModelDialog() {
    posthog.capture('mia_readiness_risk_model_opened', { risk_kind: riskKind });
    setRiskModelDialogOpen(true);
  }

  function saveThreshold(nextThreshold: number) {
    setReadinessConfigMutation.mutate({
      orgId: props.me.org.id,
      riskThreshold: nextThreshold,
    });
  }

  return (
    <>
      <BriefingCard.Root>
        <BriefingCard.Main>
          {briefingState.type === 'outreach-sent' ? (
            <>
              <BriefingCard.Eyebrow icon={faCircleCheckSolid} color="success.main">
                Outreach Sent
              </BriefingCard.Eyebrow>

              <BriefingCard.Title>
                Done — I&apos;ve reached out to{' '}
                {briefingState.outreachResult.isFullSuccess ? 'all ' : ''}
                {briefingState.outreachResult.sentCount}
              </BriefingCard.Title>

              <BriefingCard.Copy>
                <Typography variant="body1">
                  I sent confirmation requests moments ago. I&apos;ll watch for replies and update
                  the roster as patients confirm or ask to rebook.
                </Typography>
                <Typography variant="body1">
                  You&apos;ll see a note here the moment anything needs you.
                </Typography>
              </BriefingCard.Copy>
            </>
          ) : (
            <>
              <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                <BriefingCard.Eyebrow
                  icon={faMessageBot}
                  color={
                    briefingState.type === 'appointments-at-risk'
                      ? 'quaternary.light'
                      : 'success.main'
                  }
                >
                  Daily Briefing
                </BriefingCard.Eyebrow>

                <Chip
                  variant="outlined"
                  size="small"
                  icon={<FontAwesomeScalableIcon icon={faSliders} fontSize="inherit" />}
                  label="How this works"
                  onClick={openRiskModelDialog}
                />
              </Stack>

              <BriefingCard.Title>
                {greeting}, {props.me.user.displayFirstName}
              </BriefingCard.Title>

              {briefingState.type === 'appointments-at-risk' ? (
                <>
                  <BriefingCard.Copy>
                    <Typography variant="body1">
                      I found {flaggedCount} {flaggedCount === 1 ? 'appointment' : 'appointments'}{' '}
                      with the highest {attendanceRiskCopy.briefingHeadlinePhrase} in the next few
                      days.
                    </Typography>
                    <Typography variant="body1">
                      I&apos;ve already prepared the outreach I believe will have the biggest
                      impact.
                    </Typography>
                  </BriefingCard.Copy>

                  <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                    <ReviewOutreachButton
                      disabled={notContactedRows.length === 0}
                      onClick={openBulkConfirmationDialogFromBriefing}
                    />

                    <Button
                      variant="outlined"
                      color="neutral"
                      size="large"
                      startIcon={<FontAwesomeScalableIcon icon={faCircleQuestion} />}
                      onClick={openAtRiskPatientsDrawerFromBriefing}
                    >
                      {flaggedCount === 1 ? 'Why this appointment' : 'Why these appointments'}
                    </Button>
                  </Stack>
                </>
              ) : briefingState.type === 'unrankable' ? (
                <>
                  <BriefingCard.Copy>
                    <Typography variant="body1">
                      You have {unconfirmedCount}{' '}
                      {unconfirmedCount === 1 ? 'appointment' : 'appointments'} still waiting on
                      confirmation.
                    </Typography>

                    <Typography variant="body1">
                      {briefingState.reason === 'no-predictions'
                        ? "I'll start flagging risk here once predictions are available for your practice. Until then the outreach is ready whenever you are."
                        : "I couldn't load your practice's risk threshold, so I'm not ranking these yet. The outreach is ready whenever you are."}
                    </Typography>
                  </BriefingCard.Copy>

                  {/* Without a ranking there is nothing to explain, so no "why" button here — but
                      the send path stays: an unranked appointment still needs confirming. */}
                  <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                    <ReviewOutreachButton
                      disabled={notContactedRows.length === 0}
                      onClick={openBulkConfirmationDialogFromBriefing}
                    />
                  </Stack>
                </>
              ) : (
                <BriefingCard.Copy>
                  <Typography variant="body1">
                    I&apos;ll update you here when there&apos;s critical action to be taken.
                  </Typography>

                  <Typography variant="body1">
                    Things are looking good right now. Check back later.
                  </Typography>
                </BriefingCard.Copy>
              )}
            </>
          )}
        </BriefingCard.Main>

        {briefingState.type === 'appointments-at-risk' ? (
          <BriefingCard.Aside>
            <Typography variant="body2" color="text.secondary">
              <FontAwesomeScalableIcon
                icon={faChartLineUp}
                fontSize="small"
                sx={{ color: 'success.main', mr: 1, verticalAlign: 'text-bottom' }}
              />
              At your practice, patients who confirm within 24 hours are{' '}
              <Box component="span" fontWeight={600}>
                2.1x less likely
              </Box>{' '}
              to late-cancel.
            </Typography>
          </BriefingCard.Aside>
        ) : null}
      </BriefingCard.Root>

      <Card sx={{ padding: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={1}
          mb={2}
        >
          <div>
            <Typography component="h1" variant="h6">
              Patient Readiness
            </Typography>
            <Typography component="p" variant="body2" color="text.secondary">
              Flagged patients with upcoming appointments
            </Typography>
          </div>
        </Stack>

        <RadioGroup
          aria-label="Readiness buckets"
          name="readiness-bucket"
          value={selectedBucket}
          onChange={(event) => selectBucket(getSelectedBucket(event.target.value))}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          <ReadinessBucketCard.Root
            color={unconfirmedBucket.color}
            value={unconfirmedBucket.key}
            onSelect={selectUnconfirmedCard}
            data-testid={`bucket-${unconfirmedBucket.key}`}
          >
            <ReadinessBucketCard.Count value={unconfirmedCount} />

            {unconfirmedCount === 0 ? (
              <ReadinessBucketCard.Adornment>
                <ReadinessBucketCard.Status>
                  <FontAwesomeScalableIcon icon={faInfoCircle} fontSize="small" color="inherit" />
                </ReadinessBucketCard.Status>
              </ReadinessBucketCard.Adornment>
            ) : null}

            <ReadinessBucketCard.Content
              label={attendanceRiskCopy.bucketMetricLabel}
              emphasis={unconfirmedBucket.emphasis}
            />

            <ReadinessBucketCard.Divider />

            <ReadinessBucketCard.Action>
              <ReadinessCtaButton
                me={props.me}
                color={unconfirmedBucket.color}
                label="Review Outreach"
                rows={notContactedRows}
                actionScope="card"
                isCardSelected={selectedBucket === unconfirmedBucket.key}
                onSelectCard={selectUnconfirmedCard}
                disabled={notContactedRows.length === 0}
                isDialogOpen={isBulkConfirmationDialogOpen}
                onDialogOpenChange={handleBulkConfirmationDialogOpenChange}
                onHandled={handleBulkConfirmationHandled}
              />
            </ReadinessBucketCard.Action>

            <ReadinessBucketCard.AutoRun checked={false} onChange={openAutoRunDialog} />
          </ReadinessBucketCard.Root>

          <ReadinessBucketCard.Root
            color={rescheduleBucket.color}
            value={rescheduleBucket.key}
            onSelect={selectRescheduleCard}
            data-testid={`bucket-${rescheduleBucket.key}`}
          >
            <ReadinessBucketCard.Count value={rescheduleCount} />
            <ReadinessBucketCard.Content
              label={rescheduleBucket.metricLabel}
              emphasis={rescheduleBucket.emphasis}
            />
            <ReadinessBucketCard.Divider />

            <ReadinessBucketCard.Action>
              <RebookingCtaButton
                me={props.me}
                label="Review Outreach"
                actionScope="card"
                actionableRows={actionableRescheduleRows}
                totalRowCount={rescheduleCount}
                isCardSelected={selectedBucket === rescheduleBucket.key}
                onSelectCard={selectRescheduleCard}
                disabled={actionableRescheduleRows.length === 0}
              />
            </ReadinessBucketCard.Action>
          </ReadinessBucketCard.Root>

          <ReadinessBucketCard.Root
            color={waitlistBucket.color}
            value={waitlistBucket.key}
            data-testid={`bucket-${waitlistBucket.key}`}
          >
            <ReadinessBucketCard.Count value={slotFillEnabled ? offerableSlotRows.length : '--'} />
            <ReadinessBucketCard.Content
              label={waitlistBucket.metricLabel}
              emphasis={waitlistBucket.emphasis}
            />
            <ReadinessBucketCard.Divider />
          </ReadinessBucketCard.Root>
        </RadioGroup>

        <Box mt={2}>
          {selectedBucket === 'unconfirmed' ? (
            <UnconfirmedDetailsPanel me={props.me} rows={unconfirmedRows} />
          ) : selectedBucket === 'reschedule' ? (
            <RescheduleDetailsPanel me={props.me} rows={rescheduleRows} />
          ) : slotFillEnabled && slotFillData ? (
            <OfferableSlotsDetailsPanel
              orgId={props.me.org.id}
              offerBatchSize={slotFillData.offerBatchSize}
              slots={offerableSlotRows}
            />
          ) : (
            <SlotFillUnavailableDetails />
          )}
        </Box>
      </Card>

      {isAutoRunDialogOpen ? (
        <MiaProWaitlistDialog
          me={props.me}
          bucket={unconfirmedBucket.key}
          onClose={() => setAutoRunDialogOpen(false)}
        />
      ) : null}

      {briefingState.type === 'appointments-at-risk' ? (
        <DataAgent.Drawer open={isAtRiskPatientsDrawerOpen} onClose={closeAtRiskPatientsDrawer}>
          <DataAgent.Iframe
            initialPrivateContext={dataAgentInitialPrivateContext}
            initialPrompt={dataAgentInitialPrompt}
            isInitialAutoScrollEnabled={false}
            onResponse={handleAtRiskPatientsAutoDetectResponse}
          />
        </DataAgent.Drawer>
      ) : null}

      {isRiskModelDialogOpen ? (
        <RiskModelDialog
          onClose={() => setRiskModelDialogOpen(false)}
          orgName={props.me.org.name}
          explainerState={toExplainerState(riskModelExplainerQuery)}
          thresholdState={thresholdState}
          riskProbabilities={riskProbabilities}
          canEdit={canEditThreshold}
          isSaving={setReadinessConfigMutation.isPending}
          onSaveThreshold={saveThreshold}
        />
      ) : null}
    </>
  );
}

// One shared label format so every datetime across the readiness grids and dialogs reads the
// same. Accepts the ISO strings the API returns as well as the Dates the grid columns sort on.
const readinessDateTimeFormat = 'LLL d, h:mmaaa';

function formatReadinessDateTime(date: string | Date): string {
  return format(typeof date === 'string' ? new Date(date) : date, readinessDateTimeFormat);
}

function formatPersonName(person: { firstName?: string | null; lastName?: string | null }) {
  const name = [person.firstName, person.lastName].filter(Boolean).join(' ');
  return name || '--';
}

function formatUnconfirmedPatientName(row: UnconfirmedRow) {
  return formatPersonName({ firstName: row.patientFirstName, lastName: row.patientLastName });
}

// Bulk sends generate no preview and each recipient's channel is resolved server-side per
// patient (stated preference, else SMS first), so the copy can't promise a single channel.
const bulkChannelLabel = 'SMS or email';

const channelDisplayLabels = { sms: 'SMS', email: 'email' } as const satisfies Record<
  MiaMessageChannel,
  string
>;

// The bulk template takes after the deterministic fallback in care-coordinator-agent
// (apps/api/src/skills/confirmation-reminder.ts), but it is illustrative only — the actual message
// is always written server-side at send time.
// It's perfectly fine if this wording drifts from the server template; don't feel obligated to
// keep it word-for-word in sync.

// The confirm link is tokenized server-side at send time, so template copy renders it as a
// bracketed placeholder — same convention as the template's per-recipient fields.
const confirmationLinkPlaceholder = '[Confirmation Link]';

function getBulkMessageIntroduction(senderFirstName: User['firstName'], orgName: string): string {
  return `Hi [Patient Name], this is ${senderFirstName} with ${orgName}.`;
}

function getBulkConfirmationMessageTemplate(
  orgName: string,
  senderFirstName: User['firstName']
): string {
  return `${getBulkMessageIntroduction(senderFirstName, orgName)} I noticed you haven't confirmed your appointment with [Provider] on [Appointment Date]. Will you be there? Please click this link to confirm: ${confirmationLinkPlaceholder}`;
}

function getSendMessagesConfirmLabel(recipientCount: number): string {
  return recipientCount > 1 ? `Send ${recipientCount} messages` : 'Send message';
}

function getConfirmationDialogTitle(recipientCount: number): string {
  return recipientCount > 1
    ? `Send all ${recipientCount} confirmation requests`
    : 'Send confirmation request';
}

// The vetted copy the single-send dialog previewed, carried from the dialog to the send so the
// dispatched message is verbatim what staff saw.
type PreparedMessage = Pick<PreviewMessageContentResponse, 'body' | 'subject' | 'channel'>;

function notifySinglePreparedOutcome(response: SendPreparedResponse) {
  if (response.outcome === 'sent') {
    enqueueSnackbar('Sent 1 confirmation request.', { variant: 'success' });
    return;
  }

  enqueueSnackbar('1 confirmation request could not be sent.', { variant: 'warning' });
}

function notifySendRemindersOutcome(response: SendRemindersResponse) {
  if (response.sent.length > 0) {
    enqueueSnackbar(
      `Sent ${response.sent.length} confirmation ${response.sent.length === 1 ? 'request' : 'requests'}.`,
      { variant: 'success' }
    );
  }

  const unsentCount = response.failed.length + response.skipped.length;
  if (unsentCount > 0) {
    enqueueSnackbar(
      `${unsentCount} confirmation ${unsentCount === 1 ? 'request' : 'requests'} could not be sent.`,
      { variant: 'warning' }
    );
  }
}

function ReadinessCtaButton(props: {
  me: GetMeResponsePayload;
  label: string;
  rows: UnconfirmedRow[];
  color: Exclude<NonNullable<ButtonProps['color']>, 'inherit'>;
  size?: ButtonProps['size'];
  onHandled?: (response: SendRemindersResponse) => void;
  actionScope: 'card' | 'row';
  // The row scope only ever renders once its owning card is already selected (it's mounted from
  // the details panel below the bucket cards), so that call site can pass this as a static true.
  isCardSelected: boolean;
  // Card-scoped only: selects the enclosing card instead of firing the CTA when it's clicked
  // before being selected.
  onSelectCard?: () => void;
  disabled?: boolean;
  isDialogOpen?: boolean;
  onDialogOpenChange?: (isOpen: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const readinessQuery = patientReadinessDetails({ orgId: props.me.org.id });
  const [isInternalDialogOpen, setInternalDialogOpen] = React.useState(false);
  const isConfirmationDialogOpen = props.isDialogOpen ?? isInternalDialogOpen;
  const isBulk = props.rows.length > 1;
  const soleRow = props.rows[0];

  function setConfirmationDialogOpen(isOpen: boolean) {
    if (props.isDialogOpen === undefined) {
      setInternalDialogOpen(isOpen);
    }

    props.onDialogOpenChange?.(isOpen);
  }

  // Bulk-only: reached solely by the bulk dialog, which mounts only for the card scope with more
  // than one row. Single-row sends (both the row scope and the single-row card) go through
  // `sendPreparedMutation` instead.
  const sendRemindersMutation = useMutation({
    mutationFn: (request: SendRemindersRequest) => sendReminders(request),
    onSuccess: (response) => {
      notifySendRemindersOutcome(response);

      // A bulk send moves many rows at once — let the server recompute the worklist rather than
      // hand-patching every row's outreach state.
      void queryClient.invalidateQueries({ queryKey: readinessQuery.queryKey });

      props.onHandled?.(response);
      setConfirmationDialogOpen(false);
    },
    onError: () => {
      enqueueSnackbar('Could not send these confirmation requests. Please try again later.', {
        variant: 'error',
      });
    },
  });

  const sendPreparedMutation = useMutation({
    mutationFn: (request: SendPreparedRequest) => sendPrepared(request),
    onSuccess: (response) => {
      notifySinglePreparedOutcome(response);

      // A skipped/failed outcome is terminal for this send (invalid address, missing recipient,
      // dispatch failure) — leave the dialog open with the warning so Cancel is the exit.
      if (response.outcome !== 'sent' || !soleRow) {
        return;
      }

      const sentAppointmentId = soleRow.appointmentId;

      queryClient.setQueryData<PatientReadinessDetailsResponse>(
        readinessQuery.queryKey,
        (currentData) => {
          if (!currentData) {
            return currentData;
          }

          // A verbatim single send only touches one appointment — patch just that row instead of
          // refetching the whole worklist. The send's `conversationId` is the one the worklist row
          // would carry, so the patched row satisfies the awaiting_confirmation variant;
          // `lastContactedAt` is the client clock, close enough until the next poll replaces it
          // with the server's timestamp.
          return {
            ...currentData,
            unconfirmed: currentData.unconfirmed.map((row) => {
              if (row.appointmentId !== sentAppointmentId || row.status === 'confirmed') {
                return row;
              }

              return {
                ...row,
                status: 'awaiting_confirmation' as const,
                lastContactedAt: new Date().toISOString(),
                contactCount: row.contactCount + 1,
                conversationId: response.conversationId,
              };
            }),
          };
        }
      );

      setConfirmationDialogOpen(false);
    },
    onError: () => {
      enqueueSnackbar('Could not send this confirmation request. Please try again later.', {
        variant: 'error',
      });
    },
  });

  function openConfirmationDialog() {
    // Card-scoped CTAs live inside a selectable card — the first click on an inactive card
    // should only select it, not also fire the CTA.
    if (!props.isCardSelected) {
      props.onSelectCard?.();
      return;
    }

    if (props.rows.length === 0) {
      enqueueSnackbar('There are no appointments to confirm.', { variant: 'error' });
      return;
    }

    posthog.capture('mia_readiness_confirmation_dialog_opened', {
      bucket: 'unconfirmed',
      appointment_count: props.rows.length,
      action_scope: props.actionScope,
    });
    setConfirmationDialogOpen(true);
  }

  function closeConfirmationDialog() {
    setConfirmationDialogOpen(false);
  }

  function confirmAndSend() {
    const appointmentIds = props.rows.map((row) => row.appointmentId);
    posthog.capture('mia_readiness_confirmation_requests_sent', {
      bucket: 'unconfirmed',
      appointment_count: appointmentIds.length,
      action_scope: props.actionScope,
    });
    sendRemindersMutation.mutate({ orgId: props.me.org.id, appointmentIds });
  }

  // The single-send branch dispatches the exact copy the dialog previewed via `sendPrepared`,
  // rather than routing through the bulk `sendReminders` path that regenerates copy server-side.
  function confirmSinglePrepared(prepared: PreparedMessage) {
    if (!soleRow) {
      return;
    }

    posthog.capture('mia_readiness_confirmation_requests_sent', {
      bucket: 'unconfirmed',
      appointment_count: 1,
      action_scope: props.actionScope,
    });
    sendPreparedMutation.mutate({
      link: { topic: 'appointment-confirmation', appointmentId: soleRow.appointmentId },
      channel: prepared.channel,
      body: prepared.body,
      subject: prepared.subject,
    });
  }

  return (
    <>
      <Button
        variant={props.actionScope === 'card' ? 'contained' : 'outlined'}
        color={props.color}
        startIcon={<FontAwesomeScalableIcon icon={faBell} />}
        sx={{ flexShrink: 0 }}
        size={props.size ?? 'medium'}
        disabled={props.disabled}
        onClick={openConfirmationDialog}
      >
        {props.label}
      </Button>

      {isConfirmationDialogOpen && soleRow ? (
        isBulk ? (
          <RequestConfirmationDialog
            onClose={closeConfirmationDialog}
            onConfirm={confirmAndSend}
            status={sendRemindersMutation.isPending ? 'submitting' : 'idle'}
            title={getConfirmationDialogTitle(props.rows.length)}
            message={getBulkConfirmationMessageTemplate(props.me.org.name, props.me.user.firstName)}
            channelLabel={bulkChannelLabel}
            confirmLabel={getSendMessagesConfirmLabel(props.rows.length)}
            recipients={props.rows.map((row) => ({
              id: row.appointmentId,
              name: formatUnconfirmedPatientName(row),
              appointmentLabel: formatReadinessDateTime(row.appointmentStartsAt),
              providerName: row.providerName,
            }))}
          />
        ) : (
          <SingleRequestConfirmationDialog
            row={soleRow}
            onClose={closeConfirmationDialog}
            onConfirm={confirmSinglePrepared}
            isSending={sendPreparedMutation.isPending}
          />
        )
      ) : null}
    </>
  );
}

/**
 * Single-appointment confirmation dialog: previews the actual copy Mia would send (generated
 * server-side) rather than the client-side template the bulk flow shows. Mounted only while
 * open so the preview request fires exactly when the dialog appears.
 */
function SingleRequestConfirmationDialog(props: {
  row: UnconfirmedRow;
  onClose: () => void;
  onConfirm: (prepared: PreparedMessage) => void;
  isSending: boolean;
}) {
  // Match the server's send-time channel resolution: the stated preference, else SMS first.
  const requestedChannel = props.row.preferredChannel ?? 'sms';

  const previewQuery = useQuery(
    miaMessagePreview({
      kind: 'confirmation',
      appointmentId: props.row.appointmentId,
      channel: requestedChannel,
    })
  );

  // Confirm sends the previewed copy verbatim. The button is only enabled once the preview
  // succeeds, so `previewQuery.data` is present here; the server-resolved channel is used, not
  // the client's requested channel prediction.
  function confirmPrepared() {
    if (!previewQuery.isSuccess) {
      return;
    }

    props.onConfirm({
      body: previewQuery.data.body,
      subject: previewQuery.data.subject,
      channel: previewQuery.data.channel,
    });
  }

  const patientName = formatUnconfirmedPatientName(props.row);
  const appointmentLabel = formatReadinessDateTime(props.row.appointmentStartsAt);

  // Staff confirm exactly the copy they previewed, so a failed preview blocks the send (the
  // dialog's `error` status disables confirming) instead of falling back to template copy that
  // may not match what Mia would actually write.
  const message = previewQuery.isPending ? (
    <Skeleton data-testid="confirmation-message-skeleton" />
  ) : previewQuery.isError ? (
    <Typography component="span" variant="inherit" color="error.main">
      Message preview could not be loaded. Please try again later.
    </Typography>
  ) : (
    previewQuery.data.body
  );

  // The requested channel is only the client's prediction — the caption shows the channel the
  // preview actually resolved to, with an inline skeleton just for that word while it loads.
  const channelLabel = previewQuery.isPending ? (
    <Skeleton width={40} sx={{ display: 'inline-block' }} />
  ) : (
    channelDisplayLabels[previewQuery.data?.channel ?? requestedChannel]
  );

  return (
    <RequestConfirmationDialog
      onClose={props.onClose}
      onConfirm={confirmPrepared}
      status={
        props.isSending
          ? 'submitting'
          : previewQuery.isPending
            ? 'loading'
            : previewQuery.isError
              ? 'error'
              : 'idle'
      }
      title={getConfirmationDialogTitle(1)}
      message={message}
      channelLabel={channelLabel}
      confirmLabel={getSendMessagesConfirmLabel(1)}
      recipients={[
        {
          id: props.row.appointmentId,
          name: patientName,
          appointmentLabel,
          providerName: props.row.providerName,
        },
      ]}
    />
  );
}

const hasJoinedWaitlistSchema = z.boolean();

/**
 * Early Access Feature key from PostHog (Early access features → "Readiness agent actions").
 * Joining the waitlist enrolls the user in this feature.
 */
const readinessAgentActionsFeatureKey = 'readiness-agent-actions';

/**
 * Shared by `ComingSoonBucketCard` (immediate join) and `MiaProWaitlistDialog` (join from within
 * the dialog) so the two entry points read/write the same flag instead of duplicating the
 * `useLocalState` wiring.
 */
function useMiaProWaitlist(me: GetMeResponsePayload) {
  const posthog = usePostHog();
  const [hasJoinedWaitlist, setHasJoinedWaitlist] = useLocalState({
    key: `mia-readiness-actions-waitlist-${me.user.id}-${me.org.id}`,
    defaultValue: false,
    schema: hasJoinedWaitlistSchema,
  });

  function joinWaitlist() {
    // Optimistic: flip immediately and fire the PostHog call in the background. Enrollment is
    // best-effort (client-side, ad-blockable) and we don't gate the UI on it — each caller swaps
    // its own UI in response to the flag flipping instead of this hook closing anything.
    setHasJoinedWaitlist(true);

    posthog.updateEarlyAccessFeatureEnrollment(readinessAgentActionsFeatureKey, true);
  }

  return { hasJoinedWaitlist, joinWaitlist };
}

function ComingSoongChip(props: Pick<ChipProps, 'color'>) {
  return (
    <Chip
      label="COMING SOON"
      color={props.color}
      size="small"
      variant="outlined"
      icon={<FontAwesomeScalableIcon icon={faSparkles} />}
    />
  );
}

/**
 * A readiness bucket rendered as a "coming soon" teaser with an early-access join CTA. Not
 * currently mounted — retained as the reusable treatment for the next bucket that ships behind
 * early access; wire it in place of a `ReadinessBucketCard.Root` when that card lands.
 *
 * @public — intentionally exported-but-unused for now; keeps knip from flagging the retained
 * treatment as dead code.
 */
export function ComingSoonBucketCard(props: {
  me: GetMeResponsePayload;
  bucket: typeof rescheduleBucket | typeof waitlistBucket;
  count: React.ReactNode;
}) {
  const { hasJoinedWaitlist, joinWaitlist } = useMiaProWaitlist(props.me);

  return (
    <ReadinessBucketCard.Root
      color={props.bucket.color}
      value={props.bucket.key}
      data-testid={`bucket-${props.bucket.key}`}
    >
      <ReadinessBucketCard.Count value={props.count} />

      <ReadinessBucketCard.Adornment>
        <ComingSoongChip color={props.bucket.color} />
      </ReadinessBucketCard.Adornment>

      <ReadinessBucketCard.Content
        label={props.bucket.metricLabel}
        emphasis={props.bucket.emphasis}
      />

      <ReadinessBucketCard.Divider />

      <ReadinessBucketCard.Action>
        {hasJoinedWaitlist ? (
          <Typography variant="caption" color="success.main">
            You&apos;re on the early access list!
          </Typography>
        ) : (
          <Button
            color={props.bucket.color}
            variant="contained"
            startIcon={<FontAwesomeScalableIcon icon={faBolt} />}
            onClick={joinWaitlist}
          >
            Express Interest in Early Access
          </Button>
        )}
      </ReadinessBucketCard.Action>
    </ReadinessBucketCard.Root>
  );
}

const dataGridAutosizeOptions = { expand: true } as const;
const detailsEstimatedRowHeight = 72;

function getDetailsRowHeight() {
  return 'auto' as const;
}

function ReadinessDataGrid<TRow extends GridValidRowModel>(props: {
  rows: TRow[];
  columns: GridColDef<TRow>[];
  sortModel: GridSortModel;
  getRowId?: (row: TRow) => number;
  csvFileName: string;
  color: 'error' | 'warning' | 'quaternary';
}) {
  return (
    <DataGridPro
      rows={props.rows}
      columns={props.columns}
      getRowId={props.getRowId}
      hideFooter
      showToolbar
      disableRowSelectionOnClick
      disableColumnSelector
      disableColumnFilter
      autosizeOnMount
      autosizeOptions={dataGridAutosizeOptions}
      getRowHeight={getDetailsRowHeight}
      getEstimatedRowHeight={() => detailsEstimatedRowHeight}
      sx={(theme) => ({
        // Border colors
        '--DataGrid-rowBorderColor': theme.palette.divider,
        borderColor: 'divider',
        borderTopColor: `${props.color}.main`,
        [`& .${gridClasses.withBorderColor}`]: {
          borderColor: 'divider',
        },
        [`& .${gridClasses.toolbar}`]: {
          borderBottomColor: 'divider',
        },
        [`& .${gridClasses.columnHeader} .${gridClasses.columnSeparator}`]: {
          color: 'divider',
        },
        // Cell padding and alignment. The risk-reason cell is exempt from centering — its chips
        // wrap onto multiple lines and manage their own alignment.
        [`& .${gridClasses.cell}`]: { py: 1, alignItems: 'center', display: 'flex' },
      })}
      initialState={{
        sorting: { sortModel: props.sortModel },
      }}
      slotProps={{
        toolbar: {
          csvOptions: { fileName: props.csvFileName },
          printOptions: { disableToolbarButton: true },
        },
      }}
    />
  );
}

function computeRiskReasonChipDetails(row: UnconfirmedRow): string[] {
  return [
    row.riskFactors.noShowCount > 0 ? 'Past No Show' : null,
    row.riskFactors.lateCancelCount > 0 ? 'Past Late Cancel' : null,
    row.riskFactors.isNewPatient ? 'New Patient' : null,
  ].filter((reason) => reason !== null);
}

// The risk-reason column reads this from both `valueFormatter` (CSV/copy export) and
// `renderCell` (the visible chips) for the same row — cache by row identity so it's computed once.
const riskReasonChipDetailsByRow = new WeakMap<UnconfirmedRow, string[]>();

function getRiskReasonChipDetails(row: UnconfirmedRow): string[] {
  const cached = riskReasonChipDetailsByRow.get(row);
  if (cached) {
    return cached;
  }

  const details = computeRiskReasonChipDetails(row);
  riskReasonChipDetailsByRow.set(row, details);
  return details;
}

const statusCellTimeFormat = 'hh:mm aa';

// "Delivered" (not "awaiting confirmation") per design: the cell reports what happened to the
// outreach, while the pending outcome is implied by the row still counting as at-risk.
function getConfirmationStatusLabel(row: UnconfirmedRow): string {
  switch (row.status) {
    case 'confirmed':
      return `Confirmed · ${format(new Date(row.confirmedAt), statusCellTimeFormat)}`;
    case 'awaiting_confirmation':
      return `Sent · ${format(new Date(row.lastContactedAt), statusCellTimeFormat)}`;
    case 'not_contacted':
      return '--';
  }
}

const columns: GridColDef<UnconfirmedRow>[] = [
  {
    field: 'patientName',
    headerName: 'Patient',
    valueGetter: (_value, row) => formatUnconfirmedPatientName(row),
  },
  {
    field: 'appointmentDate',
    headerName: 'Appointment',
    valueGetter: (_value, row) => new Date(row.appointmentStartsAt),
    valueFormatter: (value: Date) => formatReadinessDateTime(value),
  },
  {
    field: 'providerName',
    headerName: 'Provider',
    // Blank provider names exist upstream; '--' matches how blank patient names render.
    valueGetter: (_value, row) => row.providerName || '--',
  },
  {
    field: 'appointmentTypeName',
    headerName: 'Type',
  },
  {
    field: 'attendanceRate',
    headerName: 'Risk Reason',
    maxWidth: 260,
    sortable: false,
    // The cell shows the qualitative reasons drawn from the patient's history. The value carries the
    // model's probability so an export says what this column stands for — but unscored rows hold a
    // negative sort sentinel, which the formatter blanks rather than exporting as a probability.
    // The grid's own order comes from the fetch boundary, since the column is not sortable.
    valueGetter: (_value, row) => getUnconfirmedRowRiskSortValue(row),
    valueFormatter: (_value, row) => getUnconfirmedRowRiskProbability(row) ?? '',
    renderCell: (params) => {
      const details = getRiskReasonChipDetails(params.row);

      if (details.length === 0) {
        return '--';
      }

      return (
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center">
          {details.map((reason) => (
            <Chip key={reason} label={reason} size="small" variant="outlined" />
          ))}
        </Stack>
      );
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 200,
    valueFormatter: (_value, row) => getConfirmationStatusLabel(row),
    renderCell: (
      // The array's `GridColDef<UnconfirmedRow>` context leaves the value generics at `any`, so
      // type the param to consume `valueFormatter`'s output instead of re-deriving the label.
      params: GridRenderCellParams<
        UnconfirmedRow,
        UnconfirmedRowStatus,
        ReturnType<typeof getConfirmationStatusLabel>
      >
    ) =>
      params.row.status === 'not_contacted' ? (
        <Typography variant="inherit" color="text.secondary">
          {params.formattedValue}
        </Typography>
      ) : (
        <Chip
          label={params.formattedValue}
          size="small"
          variant="outlined"
          color={params.row.status === 'confirmed' ? 'success' : 'info'}
        />
      ),
  },
];

function UnconfirmedDetailsPanel(props: { me: GetMeResponsePayload; rows: UnconfirmedRow[] }) {
  const detailsColumns = React.useMemo<GridColDef<UnconfirmedRow>[]>(
    () => [
      ...columns,
      {
        field: 'readinessAction',
        headerName: 'Action',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        minWidth: 180,
        // Always rendered so contacted rows don't leave a gap in the column — just disabled
        // once the Status cell reflects the action already taken.
        renderCell: (params) => (
          <ReadinessCtaButton
            me={props.me}
            label="Review Outreach"
            rows={[params.row]}
            color="error"
            size="small"
            actionScope="row"
            // Only ever mounted while this card (unconfirmed) is the selected bucket — the
            // details panel that renders this grid is itself gated on that selection.
            isCardSelected
            disabled={params.row.status !== 'not_contacted'}
          />
        ),
      },
    ],
    [props.me]
  );

  return (
    <Stack direction="column" spacing={2} minHeight={0}>
      <ReadinessDataGrid
        rows={props.rows}
        columns={detailsColumns}
        getRowId={(row) => row.appointmentId}
        sortModel={[
          { field: 'attendanceRate', sort: 'desc' },
          { field: 'appointmentDate', sort: 'asc' },
        ]}
        csvFileName="unconfirmed"
        color="error"
      />
    </Stack>
  );
}

const rescheduleBaseColumns: GridColDef<RescheduleRow>[] = [
  {
    field: 'patientName',
    headerName: 'Patient',
    valueGetter: (_value, row) => formatPersonName(row.patient),
  },
  {
    field: 'appointmentDate',
    headerName: 'Appointment Missed',
    valueGetter: (_value, row) => new Date(row.startDate),
    valueFormatter: (value: Date) => formatReadinessDateTime(value),
  },
  {
    field: 'providerName',
    headerName: 'Provider',
    valueGetter: (_value, row) => formatPersonName(row.provider),
  },
  {
    field: 'eventAt',
    headerName: 'Needs Rebooking Since',
    valueGetter: (_value, row) => new Date(row.eventAt),
    valueFormatter: (value: Date) => formatReadinessDateTime(value),
  },
];

function getBulkRebookingMessageTemplate(
  org: Pick<Org, 'name' | 'formattedPhone'>,
  senderFirstName: User['firstName']
): string {
  const callToAction = org.formattedPhone
    ? `You can also call the office at ${org.formattedPhone} to reschedule.`
    : 'You can also call the office to reschedule.';

  return `${getBulkMessageIntroduction(senderFirstName, org.name)} It looks like your last appointment time didn't work for you. Would you like me to add you to [Provider]'s waitlist? ${callToAction}`;
}

function notifyAskAboutRebookingOutcome(
  response: AskAboutRebookingResponse,
  requestedCount: number
) {
  if (response.sent.length > 0) {
    enqueueSnackbar(
      `Sent rebooking outreach to ${response.sent.length} ${response.sent.length === 1 ? 'patient' : 'patients'}.`,
      { variant: 'success' }
    );
  }

  // Unknown, cross-org, or newly ineligible appointments are omitted from every response
  // partition, so derive the unsent count from the original request rather than the two explicit
  // failure partitions.
  const unsentCount = requestedCount - response.sent.length;
  if (unsentCount > 0) {
    enqueueSnackbar(
      `${unsentCount} rebooking outreach ${unsentCount === 1 ? 'message' : 'messages'} could not be sent.`,
      { variant: 'warning' }
    );
  }
}

function notifySingleRebookingOutcome(response: SendPreparedResponse) {
  if (response.outcome === 'sent') {
    enqueueSnackbar('Sent rebooking outreach to 1 patient.', { variant: 'success' });
    return;
  }

  enqueueSnackbar('1 rebooking outreach message could not be sent.', { variant: 'warning' });
}

interface RebookingCtaButtonCommonProps {
  me: GetMeResponsePayload;
  label: string;
  disabled?: boolean;
}

interface RebookingCardCtaButtonProps extends RebookingCtaButtonCommonProps {
  actionScope: 'card';
  actionableRows: RescheduleRow[];
  totalRowCount: number;
  isCardSelected: boolean;
  onSelectCard: () => void;
}

interface RebookingRowCtaButtonProps extends RebookingCtaButtonCommonProps {
  actionScope: 'row';
  row: RescheduleRow;
}

type RebookingCtaButtonProps = RebookingCardCtaButtonProps | RebookingRowCtaButtonProps;

function RebookingCtaButton(props: RebookingCtaButtonProps) {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const readinessQuery = patientReadinessDetails({ orgId: props.me.org.id });
  const [isDialogOpen, setDialogOpen] = React.useState(false);
  const actionableRows = props.actionScope === 'card' ? props.actionableRows : [props.row];
  const totalRowCount = props.actionScope === 'card' ? props.totalRowCount : 1;
  const excludedCount = totalRowCount - actionableRows.length;
  const isBulk = props.actionScope === 'card';
  const soleRow = props.actionScope === 'row' ? props.row : actionableRows[0];

  const askAboutRebookingMutation = useMutation({
    mutationFn: askAboutRebooking,
    onSuccess: (response, request) => {
      notifyAskAboutRebookingOutcome(response, request.appointmentIds.length);
      void queryClient.invalidateQueries({ queryKey: readinessQuery.queryKey });
      setDialogOpen(false);
    },
    onError: () => {
      enqueueSnackbar('Could not send rebooking outreach. Please try again later.', {
        variant: 'error',
      });
    },
  });

  // Single-appointment rebooking dispatches the exact copy the dialog previewed via `sendPrepared`,
  // rather than routing through the bulk `/mia/rebooking/ask` path that regenerates copy per
  // recipient server-side. Reached only from a row action; card actions always use the bulk path.
  const sendPreparedMutation = useMutation({
    mutationFn: (request: SendPreparedRequest) => sendPrepared(request),
    onSuccess: (response) => {
      notifySingleRebookingOutcome(response);

      // A skipped/failed outcome is terminal for this send (invalid address, missing recipient,
      // dispatch failure) — leave the dialog open with the warning so Cancel is the exit.
      if (response.outcome !== 'sent' || !soleRow) {
        return;
      }

      const sentAppointmentId = soleRow.id;

      queryClient.setQueryData<PatientReadinessDetailsResponse>(
        readinessQuery.queryKey,
        (currentData) => {
          if (!currentData) {
            return currentData;
          }

          // A verbatim single send only touches one appointment — patch just that row to
          // `contacted` instead of refetching the whole worklist, matching the bulk path's
          // per-sent-row patch.
          return {
            ...currentData,
            reschedule: currentData.reschedule.map((row) =>
              row.id === sentAppointmentId ? { ...row, status: 'contacted' as const } : row
            ),
          };
        }
      );

      setDialogOpen(false);
    },
    onError: () => {
      enqueueSnackbar('Could not send rebooking outreach. Please try again later.', {
        variant: 'error',
      });
    },
  });

  function openDialog() {
    if (props.actionScope === 'card' && !props.isCardSelected) {
      props.onSelectCard();
      return;
    }

    if (actionableRows.length === 0) {
      enqueueSnackbar('There are no patients to contact about rebooking.', {
        variant: 'error',
      });
      return;
    }

    posthog.capture('mia_readiness_rebooking_dialog_opened', {
      bucket: 'reschedule',
      appointment_count: actionableRows.length,
      excluded_count: excludedCount,
      action_scope: props.actionScope,
    });
    setDialogOpen(true);
  }

  function confirmAndSend() {
    const request = {
      orgId: props.me.org.id,
      appointmentIds: actionableRows.map((row) => row.id),
    };
    posthog.capture('mia_readiness_rebooking_outreach_sent', {
      bucket: 'reschedule',
      appointment_count: request.appointmentIds.length,
      excluded_count: excludedCount,
      action_scope: props.actionScope,
    });
    askAboutRebookingMutation.mutate(request);
  }

  function confirmSinglePrepared(prepared: PreparedMessage) {
    if (!soleRow) {
      return;
    }

    posthog.capture('mia_readiness_rebooking_outreach_sent', {
      bucket: 'reschedule',
      appointment_count: 1,
      action_scope: props.actionScope,
    });
    sendPreparedMutation.mutate({
      link: { topic: 'rebooking', appointmentId: soleRow.id },
      channel: prepared.channel,
      body: prepared.body,
      subject: prepared.subject,
    });
  }

  return (
    <>
      <Button
        variant={props.actionScope === 'card' ? 'contained' : 'outlined'}
        size={props.actionScope === 'card' ? 'medium' : 'small'}
        startIcon={<FontAwesomeScalableIcon icon={faCalendarPlus} />}
        disabled={props.disabled}
        color="warning"
        aria-label={
          props.actionScope === 'row' && soleRow
            ? `${props.label} for ${formatPersonName(soleRow.patient)}`
            : props.actionScope === 'card'
              ? `${props.label} for rebooking`
              : undefined
        }
        onClick={openDialog}
      >
        {props.label}
      </Button>

      {isDialogOpen && soleRow ? (
        isBulk ? (
          <RebookDialog
            onClose={() => setDialogOpen(false)}
            onConfirm={confirmAndSend}
            status={askAboutRebookingMutation.isPending ? 'submitting' : 'idle'}
            confirmLabel={getSendMessagesConfirmLabel(actionableRows.length)}
            template={getBulkRebookingMessageTemplate(props.me.org, props.me.user.firstName)}
            recipients={actionableRows.map(mapRebookingRowToDialogRecipient)}
            excludedCount={excludedCount}
          />
        ) : (
          <SingleRebookDialog
            row={soleRow}
            onClose={() => setDialogOpen(false)}
            onConfirm={confirmSinglePrepared}
            isSending={sendPreparedMutation.isPending}
          />
        )
      ) : null}
    </>
  );
}

function mapRebookingRowToDialogRecipient(row: RescheduleRow) {
  return {
    id: row.id,
    name: formatPersonName(row.patient),
    appointmentLabel: formatReadinessDateTime(row.startDate),
    providerName: formatPersonName(row.provider),
  };
}

/**
 * Single-appointment rebooking dialog: previews the actual copy Mia would send (generated
 * server-side) and dispatches it verbatim. This UI keeps rebooking SMS-only, so it previews and
 * sends the SMS and nothing else — even though the server can now generate and dispatch email
 * rebooking copy too. TODO(multi-channel): going multi-channel here means resolving the channel
 * from the row's contact info (or previewing both) instead of the `channel` const below. Mounted
 * only while open so the preview request fires exactly when the dialog appears.
 */
function SingleRebookDialog(props: {
  row: RescheduleRow;
  onClose: () => void;
  onConfirm: (prepared: PreparedMessage) => void;
  isSending: boolean;
}) {
  const channel: MiaMessageChannel = 'sms';

  const previewQuery = useQuery(
    miaMessagePreview({ kind: 'rebooking', appointmentId: props.row.id, channel })
  );

  // Confirm dispatches the previewed copy verbatim. The button is only enabled once the preview
  // succeeds, so `previewQuery.data` is present here; the server-resolved channel is used.
  function confirmPrepared() {
    if (!previewQuery.isSuccess) {
      return;
    }

    props.onConfirm({
      body: previewQuery.data.body,
      subject: previewQuery.data.subject,
      channel: previewQuery.data.channel,
    });
  }

  // Staff send exactly the copy they previewed, so a failed preview blocks the send (the dialog's
  // `error` status disables confirming) rather than falling back to copy Mia may not actually write.
  const body = previewQuery.isPending ? (
    <Skeleton data-testid="rebooking-message-skeleton" />
  ) : previewQuery.isError ? (
    <Typography component="span" variant="inherit" color="error.main">
      Message preview could not be loaded. Please try again later.
    </Typography>
  ) : (
    previewQuery.data.body
  );

  return (
    <RebookDialog
      onClose={props.onClose}
      onConfirm={confirmPrepared}
      status={
        props.isSending
          ? 'submitting'
          : previewQuery.isPending
            ? 'loading'
            : previewQuery.isError
              ? 'error'
              : 'idle'
      }
      confirmLabel={getSendMessagesConfirmLabel(1)}
      recipient={mapRebookingRowToDialogRecipient(props.row)}
      preview={{
        // Label the channel the preview actually resolved to, not the requested `channel` const —
        // the send dispatches `previewQuery.data.channel`, so the label must track the same source.
        channel: previewQuery.data?.channel ?? channel,
        body,
        subject: previewQuery.data?.subject,
      }}
    />
  );
}

const rebookingStatusDetails: Record<RebookingCandidateStatus, { label: string }> = {
  needs_outreach: { label: 'Needs outreach' },
  contacted: { label: 'Contacted' },
  waitlisted: { label: 'Waitlisted' },
  staff_follow_up: { label: 'Staff follow-up' },
};

function RebookingStatusCell(props: { status: RebookingCandidateStatus }) {
  const details = rebookingStatusDetails[props.status];
  return <Chip label={details.label} size="small" variant="outlined" />;
}

function RebookingActionCell(props: { me: GetMeResponsePayload; row: RescheduleRow }) {
  // TODO(multi-channel): phone-only on purpose (see isSmsReachableRebookingRow): an email-only
  // patient is reachable server-side but not offered outreach from this UI. When email joins the
  // gate, this check becomes "no phone AND no email" and the copy returns to "No contact method".
  if (!props.row.patientPhone) {
    return (
      <Typography variant="inherit" color="text.secondary">
        No mobile number
      </Typography>
    );
  }

  return (
    <RebookingCtaButton
      me={props.me}
      label="Review outreach"
      actionScope="row"
      row={props.row}
      disabled={props.row.status !== 'needs_outreach'}
    />
  );
}

function RescheduleDetailsPanel(props: { me: GetMeResponsePayload; rows: RescheduleRow[] }) {
  const detailsColumns = React.useMemo<GridColDef<RescheduleRow>[]>(
    () => [
      ...rescheduleBaseColumns,
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 160,
        valueGetter: (_value, row) => rebookingStatusDetails[row.status].label,
        renderCell: (params) => <RebookingStatusCell status={params.row.status} />,
      },
      {
        field: 'action',
        headerName: 'Action',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        minWidth: 200,
        renderCell: (params) => <RebookingActionCell me={props.me} row={params.row} />,
      },
    ],
    [props.me]
  );

  return (
    <Stack direction="column" spacing={2} minHeight={0}>
      <ReadinessDataGrid
        rows={props.rows}
        columns={detailsColumns}
        sortModel={[{ field: 'appointmentDate', sort: 'asc' }]}
        csvFileName="reschedule"
        color="warning"
      />
    </Stack>
  );
}

/** The claimed offer's patient name — the winner a staff Approve would book. */
function getSlotClaimantName(slotFillState: NonNullable<OfferableSlot['slotFillState']>): string {
  const claimedOffer = slotFillState.offers.find((offer) => offer.status === 'claimed');
  return formatPersonName({
    firstName: claimedOffer?.patientFirstName,
    lastName: claimedOffer?.patientLastName,
  });
}

function getSlotFillStatusLabel(slot: OfferableSlot): string {
  if (!slot.slotFillState) {
    return '--';
  }

  if (slot.slotFillState.status === 'claimed') {
    return `Claimed by ${getSlotClaimantName(slot.slotFillState)}`;
  }

  if (slot.slotFillState.status === 'filled') {
    return 'Booked';
  }

  const offerCount = slot.slotFillState.offers.length;
  return `Offered to ${offerCount} ${offerCount === 1 ? 'patient' : 'patients'}`;
}

const offerableSlotBaseColumns: GridColDef<OfferableSlot>[] = [
  {
    field: 'providerName',
    headerName: 'Provider',
    valueGetter: (_value, row) =>
      formatPersonName({ firstName: row.providerFirstName, lastName: row.providerLastName }),
  },
  {
    field: 'slotStartsAt',
    headerName: 'Slot Starts',
    valueGetter: (_value, row) => new Date(row.slotStartsAt),
    valueFormatter: (value: Date) => formatReadinessDateTime(value),
  },
  {
    field: 'slotEndsAt',
    headerName: 'Slot Ends',
    valueGetter: (_value, row) => new Date(row.slotEndsAt),
    valueFormatter: (value: Date) => formatReadinessDateTime(value),
  },
  {
    field: 'canceledAt',
    headerName: 'Canceled',
    valueGetter: (_value, row) => new Date(row.canceledAt),
    valueFormatter: (value: Date) => formatReadinessDateTime(value),
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 200,
    valueGetter: (_value, row) => getSlotFillStatusLabel(row),
    renderCell: (params) => <SlotFillStatusCell slot={params.row} />,
  },
];

function notifyOfferSlotOutcome(response: OfferSlotToWaitlistResponse) {
  if (response.offered.length === 0) {
    enqueueSnackbar('Could not reach any waitlisted candidates for this slot.', {
      variant: 'warning',
    });
    return;
  }

  enqueueSnackbar(
    response.skipped.length > 0
      ? `Offered to ${response.offered.length} ${response.offered.length === 1 ? 'patient' : 'patients'}, ${response.skipped.length} skipped.`
      : `Offered to ${response.offered.length} ${response.offered.length === 1 ? 'patient' : 'patients'}.`,
    { variant: 'success' }
  );
}

/** A status chip whose tooltip (and accessible label) lists everyone the slot was offered to. */
function OffereeTooltipChip(props: {
  label: string;
  color: ChipProps['color'];
  slotFillState: NonNullable<OfferableSlot['slotFillState']>;
}) {
  const offereeNames = props.slotFillState.offers
    .map((offer) =>
      formatPersonName({ firstName: offer.patientFirstName, lastName: offer.patientLastName })
    )
    .join(', ');

  return (
    <Tooltip title={offereeNames || 'No offerees'}>
      {/* Chip without onClick isn't focusable, which would leave the tooltip
          mouse-only — tabIndex lets keyboard focus surface it, and the
          aria-label carries the offeree names for screen readers directly. */}
      <Chip
        label={props.label}
        size="small"
        variant="outlined"
        color={props.color}
        tabIndex={0}
        aria-label={`${props.label}: ${offereeNames || 'no offerees'}`}
      />
    </Tooltip>
  );
}

/**
 * The Status cell: what happened to the slot's offer, whoever holds the claim included. The
 * actionable half (offer trigger, approve/reject) lives in the Action column. Triggered slots
 * persist in the feed until their start time passes, so `filled` is the normal terminal state of
 * every approved slot, not a transient.
 */
function SlotFillStatusCell(props: { slot: OfferableSlot }) {
  if (!props.slot.slotFillState) {
    return (
      <Typography variant="inherit" color="text.secondary">
        --
      </Typography>
    );
  }

  if (props.slot.slotFillState.status === 'claimed') {
    return (
      <Chip
        label={`Claimed by ${getSlotClaimantName(props.slot.slotFillState)}`}
        size="small"
        variant="outlined"
        color="warning"
      />
    );
  }

  if (props.slot.slotFillState.status === 'filled') {
    return (
      <OffereeTooltipChip label="Booked" color="success" slotFillState={props.slot.slotFillState} />
    );
  }

  return (
    <OffereeTooltipChip
      label={getSlotFillStatusLabel(props.slot)}
      color="info"
      slotFillState={props.slot.slotFillState}
    />
  );
}

/**
 * The claim queue's inline actions on a claimed slot. Approve books the winner's own waitlisted
 * appointment type server-side and Mia messages the patient — the UI only toasts and refetches.
 * "Reject" is the staff-facing label; the endpoint is named for what it does: release the
 * claim so the slot is back in play. A `booked: false` outcome changes nothing server-side, so
 * the row stays claimed with both actions live for a retry or an explicit release.
 */
function ClaimedSlotActions(props: {
  orgId: number;
  slotFillState: NonNullable<OfferableSlot['slotFillState']>;
}) {
  const queryClient = useQueryClient();
  const slotsQuery = offerableSlots({ orgId: props.orgId });

  const claimantName = getSlotClaimantName(props.slotFillState);

  function refetchSlots() {
    void queryClient.invalidateQueries({ queryKey: slotsQuery.queryKey });
  }

  const approveMutation = useMutation({
    mutationFn: () => approveSlotFill({ slotFillId: props.slotFillState.slotFillId }),
    onSuccess: (response) => {
      if (!response.booked) {
        // No refetch: the server changed nothing, and the row must stay claimed and actionable.
        enqueueSnackbar(getCouldntBookMessage(response.reason), { variant: 'error' });
        return;
      }

      enqueueSnackbar(`Booked this slot for ${claimantName}.`, { variant: 'success' });
      refetchSlots();
    },
    onError: (error) => {
      // A rejection means the row changed underneath (claim released/expired, org mismatch) —
      // refetch so the row reflects whatever it became.
      enqueueSnackbar(getClaimActionErrorMessage(error), { variant: 'error' });
      refetchSlots();
    },
  });

  const releaseMutation = useMutation({
    mutationFn: () => releaseSlotFillClaim({ slotFillId: props.slotFillState.slotFillId }),
    onSuccess: () => {
      enqueueSnackbar('Claim released — this slot is open to offers again.', {
        variant: 'success',
      });
    },
    onError: (error) => {
      enqueueSnackbar(getClaimActionErrorMessage(error), { variant: 'error' });
    },
    onSettled: refetchSlots,
  });

  // Both actions lock while either is in flight, and stay locked through the settle-to-refetch
  // window after an outcome that changed the row (a booking or a release) — a second click there
  // would double-book or release a claim that no longer exists. A booked:false outcome leaves the
  // row claimed, so the actions re-enable for a retry.
  const actionsLocked =
    approveMutation.isPending ||
    releaseMutation.isPending ||
    (approveMutation.isSuccess && approveMutation.data.booked) ||
    releaseMutation.isSuccess;

  return (
    <Stack direction="row" spacing={1}>
      <Button
        variant="outlined"
        size="small"
        color="quaternary"
        startIcon={<FontAwesomeScalableIcon icon={faCheck} />}
        loading={approveMutation.isPending}
        loadingPosition="start"
        disabled={actionsLocked}
        // Row-scoped label so the repeated action names the claimant it books (a11y + tests).
        aria-label={`Approve for ${claimantName}`}
        onClick={() => approveMutation.mutate()}
      >
        Approve
      </Button>

      <Button
        variant="outlined"
        size="small"
        startIcon={<FontAwesomeScalableIcon icon={faXmark} />}
        loading={releaseMutation.isPending}
        loadingPosition="start"
        disabled={actionsLocked}
        aria-label={`Reject for ${claimantName}`}
        onClick={() => releaseMutation.mutate()}
      >
        Reject
      </Button>
    </Stack>
  );
}

/**
 * Per-row Slot-Fill trigger: staff click blasts the freed slot to its waitlist candidates. Count
 * staleness is accepted — the server is authoritative and a race (slot refilled, threshold aged
 * out, …) surfaces as a rejection toast, with a refetch either way since the reach count can also
 * have moved.
 */
function OfferSlotAction(props: { orgId: number; offerBatchSize: number; slot: OfferableSlot }) {
  const queryClient = useQueryClient();
  const slotsQuery = offerableSlots({ orgId: props.orgId });

  const offerMutation = useMutation({
    mutationFn: () => offerSlotToWaitlist({ appointmentId: props.slot.appointmentId }),
    onSuccess: notifyOfferSlotOutcome,
    onError: (error) => {
      enqueueSnackbar(getOfferSlotErrorMessage(error), { variant: 'error' });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: slotsQuery.queryKey });
    },
  });

  if (props.slot.slotFillState) {
    // Post-blast, nothing actionable until a claim lands — the Status column carries the state.
    return props.slot.slotFillState.status === 'claimed' ? (
      <ClaimedSlotActions orgId={props.orgId} slotFillState={props.slot.slotFillState} />
    ) : null;
  }

  const reachCount = Math.min(props.slot.candidateCount, props.offerBatchSize);

  return (
    <Button
      variant="outlined"
      size="small"
      color="quaternary"
      startIcon={<FontAwesomeScalableIcon icon={faPaperPlane} />}
      loading={offerMutation.isPending}
      loadingPosition="start"
      // isSuccess keeps it disabled through the settle-to-refetch window — a
      // second click there would double-fire the blast. The refetched row swaps
      // this button for the state chip; a rejection re-enables it.
      disabled={reachCount === 0 || offerMutation.isPending || offerMutation.isSuccess}
      onClick={() => offerMutation.mutate()}
    >
      {`Offer to waitlist (${reachCount})`}
    </Button>
  );
}

function OfferableSlotsDetailsPanel(props: {
  orgId: number;
  offerBatchSize: number;
  slots: OfferableSlot[];
}) {
  const detailsColumns = React.useMemo<GridColDef<OfferableSlot>[]>(
    () => [
      ...offerableSlotBaseColumns,
      {
        field: 'action',
        headerName: 'Action',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        minWidth: 220,
        renderCell: (params) => (
          <OfferSlotAction
            orgId={props.orgId}
            offerBatchSize={props.offerBatchSize}
            slot={params.row}
          />
        ),
      },
    ],
    [props.orgId, props.offerBatchSize]
  );

  return (
    <Stack direction="column" spacing={2} minHeight={0}>
      <ReadinessDataGrid
        rows={props.slots}
        columns={detailsColumns}
        getRowId={(row) => row.appointmentId}
        sortModel={[{ field: 'slotStartsAt', sort: 'asc' }]}
        csvFileName="offerable-slots"
        color="quaternary"
      />
    </Stack>
  );
}

function SlotFillUnavailableDetails() {
  return (
    <Card sx={{ padding: 2, bgcolor: 'background.default', backgroundImage: 'none' }}>
      <Typography component="p" variant="body1" color="text.secondary" textAlign="center">
        Appointment slot filling isn&apos;t enabled for this organization.
      </Typography>
    </Card>
  );
}

const miaProAutoModeDescription =
  "Coordinate Pro's Auto mode monitors your schedule 24/7 — automatically sending confirmation requests to every at-risk patient the moment they're flagged, with zero manual action required.";

const miaProAutoModeHighlights = [
  'Instant outreach — patients contacted within minutes of being flagged',
  'Smart follow-up — automatic second attempt if no response in 24 hours',
  'Full audit trail — every message logged and reportable',
] as const;

const miaProWaitlistJoinedDescription =
  "We'll notify you the moment Coordinate Pro is available for your account.";

function MiaProWaitlistDialog(props: {
  me: GetMeResponsePayload;
  bucket: ReadinessBucket;
  onClose: () => void;
}) {
  const waitlistTitleId = React.useId();
  const waitlistDescriptionId = React.useId();
  const { hasJoinedWaitlist, joinWaitlist } = useMiaProWaitlist(props.me);
  const posthog = usePostHog();

  function close() {
    if (!hasJoinedWaitlist) {
      posthog.capture('mia_readiness_waitlist_dialog_dismissed', { bucket: props.bucket });
    }

    props.onClose();
  }

  return (
    <Dialog.Root
      open
      onClose={close}
      maxWidth="xs"
      fullWidth
      aria-labelledby={waitlistTitleId}
      aria-describedby={waitlistDescriptionId}
      slotProps={{
        paper: { sx: { borderWidth: 2, borderStyle: 'solid', borderColor: 'quaternary.main' } },
      }}
    >
      <Dialog.Header>
        <Stack direction="row" spacing={1.5}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 1.5,
              bgcolor: 'quaternary.main',
              color: 'quaternary.contrastText',
              flexShrink: 0,
            }}
          >
            <FontAwesomeScalableIcon icon={faBolt} fontSize="small" color="inherit" />
          </Box>

          <div>
            <Dialog.Title id={waitlistTitleId}>Coordinate Pro</Dialog.Title>
            <Dialog.Description id={waitlistDescriptionId}>
              Fully automated no-show prevention
            </Dialog.Description>
          </div>
        </Stack>
      </Dialog.Header>

      <Dialog.Content>
        {hasJoinedWaitlist ? (
          <Stack direction="column" spacing={1} alignItems="center" textAlign="center" py={2}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: 'success.main',
                color: 'success.contrastText',
              }}
            >
              <FontAwesomeScalableIcon icon={faShieldCheck} fontSize="large" color="inherit" />
            </Box>

            <Typography component="p" variant="h6">
              You&apos;re on the list!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {miaProWaitlistJoinedDescription}
            </Typography>
          </Stack>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" marginBottom={2}>
              {miaProAutoModeDescription}
            </Typography>

            <Stack direction="column" spacing={1}>
              {miaProAutoModeHighlights.map((highlight) => (
                <Stack key={highlight} direction="row" spacing={1} alignItems="flex-start">
                  <FontAwesomeScalableIcon
                    icon={faCircleCheckSolid}
                    sx={{ color: 'quaternary.main' }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {highlight}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </>
        )}
      </Dialog.Content>

      <Dialog.Footer>
        {hasJoinedWaitlist ? (
          <Dialog.DismissButton variant="contained" color="quaternary">
            Got It
          </Dialog.DismissButton>
        ) : (
          <>
            <Dialog.DismissButton />
            <Button
              variant="contained"
              color="quaternary"
              size="large"
              startIcon={<FontAwesomeScalableIcon icon={faSparkles} />}
              onClick={joinWaitlist}
            >
              Join the waitlist
            </Button>
          </>
        )}
      </Dialog.Footer>
    </Dialog.Root>
  );
}
